import { useState } from 'react';
import { Zap, MapPin, Video, Phone, Check } from 'lucide-react';
import { Modal, DateTimePicker, LimitReachedModal } from '../ui';
import { useTranslation } from '../../context/LanguageContext';
import { createMeeting } from '../../services/meetingsService';
import { lireRefusQuota } from '../../utils/refusQuota';
import {
    DUREES_EXPRESS, DUREE_EXPRESS_DEFAUT,
    construireRendezVousExpress, erreursRendezVousExpress
} from '../../utils/rendezVousExpress';

const TYPES = [
    { id: 'in_person', icone: MapPin, cle: 'typeInPerson' },
    { id: 'video', icone: Video, cle: 'typeVideo' },
    { id: 'phone', icone: Phone, cle: 'typePhone' }
];

/**
 * Rendez-vous express depuis la fiche d'un membre : quelques minutes à deux,
 * en personne, en visio ou par téléphone. C'est une réunion ordinaire du module
 * Réunions (le membre est invité et prévenu, elle s'ajoute à l'agenda).
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {number} userId - L'organisateur
 * @param {{id: number, firstName: string, lastName: string}} target - Le membre invité
 * @param {Function} onNavigate - Pour la fenêtre « limite atteinte »
 */
const ExpressMeetingModal = ({ isOpen, onClose, userId, target, onNavigate }) => {
    const { t } = useTranslation();
    const l = t.meetingsExpress || {};

    const [debut, setDebut] = useState('');
    const [minutes, setMinutes] = useState(DUREE_EXPRESS_DEFAUT);
    const [type, setType] = useState('in_person');
    const [lieu, setLieu] = useState('');
    const [lien, setLien] = useState('');
    const [message, setMessage] = useState('');
    const [erreurs, setErreurs] = useState({});
    const [erreurEnvoi, setErreurEnvoi] = useState('');
    const [envoi, setEnvoi] = useState(false);
    const [envoye, setEnvoye] = useState(false);
    const [limite, setLimite] = useState(null);

    const nomCible = `${target?.firstName || ''} ${target?.lastName || ''}`.trim();

    const fermer = () => {
        setEnvoye(false);
        setErreurs({});
        setErreurEnvoi('');
        onClose?.();
    };

    const envoyer = async () => {
        const problemes = erreursRendezVousExpress({ debut, type, lieu, lien });
        setErreurs(problemes);
        if (Object.keys(problemes).length > 0) return;

        setEnvoi(true);
        setErreurEnvoi('');
        try {
            const titre = (l.title || 'Rendez-vous express') + ' : ' + nomCible;
            const res = await createMeeting(construireRendezVousExpress({
                creatorId: userId, targetId: target.id, title: titre, debut, minutes, type, lieu, lien, message
            }));
            const quota = lireRefusQuota(res);
            if (res.success) setEnvoye(true);
            else if (quota) setLimite(quota);
            else setErreurEnvoi(res.message || l.error || '');
        } finally {
            setEnvoi(false);
        }
    };

    const libelleErreur = (cle) => ({
        debut: l.errorDate, passe: l.errorPast, lien: l.errorLink, lieu: l.errorPlace
    }[cle]);

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={fermer}
                title={l.title || 'Rendez-vous express'}
                icon={Zap}
                size="md"
                onSubmit={envoye ? fermer : envoyer}
                submitText={envoye ? (l.close || 'Fermer') : (l.send || 'Envoyer l\'invitation')}
                loading={envoi}
            >
                {envoye ? (
                    <div className="text-center py-6 space-y-3" data-testid="express-envoye">
                        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Check className="w-6 h-6 text-emerald-600" />
                        </div>
                        <p className="text-gray-800 font-medium">{(l.sent || '').replace('{name}', nomCible)}</p>
                        <p className="text-sm text-gray-500">{l.sentHint}</p>
                    </div>
                ) : (
                    <div className="space-y-5">
                        <p className="text-sm text-gray-600">{(l.intro || '').replace('{name}', nomCible)}</p>

                        {erreurEnvoi && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" role="alert">{erreurEnvoi}</div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{l.when} *</label>
                            <DateTimePicker
                                value={debut}
                                onChange={setDebut}
                                showTimeSelect={true}
                                placeholder={l.whenPlaceholder}
                                minDate={new Date()}
                                filterPassedTime={true}
                                className={erreurs.debut ? 'border-red-500' : ''}
                            />
                            {erreurs.debut && <p className="text-xs text-red-600 mt-1">{libelleErreur(erreurs.debut)}</p>}
                        </div>

                        <div>
                            <span className="block text-sm font-medium text-gray-700 mb-2">{l.duration}</span>
                            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={l.duration}>
                                {DUREES_EXPRESS.map((d) => (
                                    <button
                                        key={d}
                                        type="button"
                                        role="radio"
                                        aria-checked={minutes === d}
                                        onClick={() => setMinutes(d)}
                                        className={`px-4 py-2 min-h-11 sm:min-h-0 rounded-full text-sm font-medium border transition-colors ${
                                            minutes === d ? 'bg-cabaza-purple text-white border-cabaza-purple' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {d} min
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <span className="block text-sm font-medium text-gray-700 mb-2">{l.how}</span>
                            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={l.how}>
                                {TYPES.map(({ id, icone: Icone, cle }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        role="radio"
                                        aria-checked={type === id}
                                        onClick={() => setType(id)}
                                        className={`flex flex-col items-center gap-1 px-2 py-3 min-h-11 rounded-xl text-sm border transition-colors ${
                                            type === id ? 'bg-cabaza-purple/10 text-cabaza-purple border-cabaza-purple' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <Icone className="w-5 h-5" />
                                        {l[cle]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {type === 'in_person' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{l.place} *</label>
                                <input
                                    type="text"
                                    value={lieu}
                                    onChange={(e) => setLieu(e.target.value)}
                                    placeholder={l.placePlaceholder}
                                    className={`w-full px-4 py-2 border rounded-lg ${erreurs.lieu ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                {erreurs.lieu && <p className="text-xs text-red-600 mt-1">{libelleErreur(erreurs.lieu)}</p>}
                            </div>
                        )}
                        {type === 'video' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{l.link} *</label>
                                <input
                                    type="url"
                                    value={lien}
                                    onChange={(e) => setLien(e.target.value)}
                                    placeholder="https://"
                                    className={`w-full px-4 py-2 border rounded-lg ${erreurs.lien ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                {erreurs.lien && <p className="text-xs text-red-600 mt-1">{libelleErreur(erreurs.lien)}</p>}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{l.message}</label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={2}
                                maxLength={500}
                                placeholder={l.messagePlaceholder}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
                            />
                        </div>
                    </div>
                )}
            </Modal>

            {limite && (
                <LimitReachedModal
                    isOpen
                    onClose={() => setLimite(null)}
                    moduleCode="MEETINGS"
                    current={limite.current}
                    max={limite.max}
                    periodUnit={limite.periodUnit}
                    periodCount={limite.periodCount}
                    profileTypeId={parseInt(localStorage.getItem('userProfileTypeId'), 10)}
                    peopleUuid={localStorage.getItem('userUuid')}
                    onNavigate={(page, state) => { setLimite(null); fermer(); onNavigate?.(page, state); }}
                />
            )}
        </>
    );
};

export default ExpressMeetingModal;
